import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex';
import api from '@reserve/api'
import appapi from '@appbase/api/file'
import { uGetDateFormat, IEDate, uGetTimeArray } from '@appbase/util/date-helper'
import { FlowFileMeta } from '@appbase/components/common/FlowFileUploader/flow-file-meta'
import { invalidateUrl } from '@appbase/util';
import * as types from '@reserve/store/types'
import * as accountTypes from '@account/store/types'
import { ReserveResourceMode } from '@reserve/types';
import { getFullTimetext } from '@reserve/disp-helper';
import { handleClose, openReserveWindow } from '../../popup-handler';

import { LoadingBar } from '@appbase/components/loading';
import { EumEditor, ViewFrame } from '@appbase/components/editor'
import { FlowFile } from '@appbase/libs/flowjs';
import { FlowFileUploader } from '@appbase/components/common'
import RecipientField from '@reserve/components/RecipientField.vue';
import RecurringOption from './RecurringOption.vue';
import TimespanSelectionPane from '@reserve/components/ReserveResource/TimespanSelectionPane.vue';
import ResourceSelectionPane from '@reserve/components/ReserveResource/ResourceSelectionPane.vue';
import Attachments from './Attachments.vue';

@Component({
    components: {        
        TimespanSelectionPane,
        ResourceSelectionPane,
        RecipientField,
        FlowFileUploader,
        RecurringOption,
        EumEditor,
        ViewFrame,
        Attachments,
        LoadingBar
    },
    computed: mapGetters({
        user: accountTypes.USER,
        resourceList: types.EWS_RESOURCEGROUPS_LIST,
        resourceListDb: types.DB_RESOURCEGROUPS_LIST,
        selectedResources: types.SELECTED_RESOURCE_KEYS
    }),

    methods: mapActions({
        createEvent: types.CALENDAR_EVENT_CREATE,
        updateEvent: types.CALENDAR_EVENT_UPDATE,
        cancelEwsEvent: types.CALENDAR_EVENT_EWSCANCEL,
        cancelDbEvent: types.CALENDAR_EVENT_DBCANCEL,
        loadEvents: types.CALENDAR_EVENTLIST_LOAD
    })
})
export default class ReserveResource extends Vue {
    @Prop({ type: Boolean, default: false }) isPopup!: boolean; 

    createEvent!: (payload: { item: reserve.CalendarItem, itemStore: 'EWS' | 'DB' }) => Promise<any>;
    updateEvent!: (payload: { item: reserve.CalendarItem, itemStore: 'EWS' | 'DB', masterOccurrence: string | null }) => Promise<any>;
    cancelEwsEvent!: (id: string) => Promise<any>;
    cancelDbEvent!: (item: reserve.CalendarItem) => Promise<any>;
    loadEvents!: () => Promise<any[]>;

    user!: account.User;
    resourceList!: ewsResource.EwsResourceItem[];
    resourceListDb!: reserve.DbResourceItem[];
    selectedResources!: string[];

    mode: ReserveResourceMode = ReserveResourceMode.Write;
    contentLoaded: boolean = false;
    showRecurringOption: boolean = false;
    currentReccuringOption: reserve.CalendarItemRecurrence | null = null;
    recurrenceDesc: string = "";
    recurrenceIntervalDesc: string = "";
    dispResourceName: string = "";
    confSubject: string = "";

    myCalendarFolderId: string | null = null;
    currentReserveId: string | null = null;
    currentItem: reserve.CalendarItem | null = null;  
    curResourceItem: ewsResource.EwsResourceItem | reserve.DbResourceItem | null = null;      
    masterOccurrence: string | null = null; 
    sequence: number = 0; 

    isAlldayEvent: boolean = false;

    eventStart: string = uGetDateFormat('yyyy-MM-dd', new Date());
    eventStartTime: string = "";
    eventEnd: string = uGetDateFormat('yyyy-MM-dd', new Date());
    eventEndTime: string = "";
    initCompany: string = '';
    initCategory: 'FAVORITE' | 'EWS' | 'DB' | null = null;
    initResource: string | null = null;
    dispEventTime: string = "";
    timeMounted: boolean = false;

    requiredRecipients: orgmap.UserItem[] = [];
    optionalRecipients: orgmap.UserItem[] = [];    

    uploadUrl: string = invalidateUrl('/cm/file/store/api/flow');
    deleteUrl: string = invalidateUrl('/cm/file/store/api/delete');
    sizeLimit: number | null = null;
    bannedExtentions: string[] = [];    
    isFileUploading: boolean = false;        
    isLoading: boolean = false;
    files: FlowFile[] = [];
    uploaderConfig: FlowFileMeta = {
        code: '',
        codeValues: [],
        mode: 'create'
    };

    fileCode: string | null = null;
    calendarFiles: ewsResource.EwsAttachment[] | appbase.FileInfo[] = [];
    reserveContent: string = "";
    
    synapopts: editor.synap.SynapEditorConfig = {        
        'editor.autoSave': false,
        'editor.autoSave.period' : 10,
        'editor.menu.show' : true,                     
    }      
    tinyopts: any = {
        content_style: `
                        p { font-family: dotum; }
                        body {font-family: dotum; font-size: 10pt;}
                        body#tinymce p {
                            font-family: dotum; 
                            margin-top: 0.3em;
                            margin-bottom: 0.3em;
                        }`,
        language: "ko_KR",
    };  

    created() {
        var { comCode, itemStore, reserveId, resource } = this.$route.query;
    
        if(comCode) {
            this.initCompany = comCode.toString();
        }

        if(itemStore) {
            this.initCategory = itemStore.toString() as 'FAVORITE' | 'EWS' | 'DB';
        }

        if(resource) {
            this.initResource = resource.toString();
        }

        if(reserveId && resource) {     
            api.resource.getResource(itemStore.toString(), resource.toString()).then(res => {
                this.curResourceItem = res as (ewsResource.EwsResourceItem | reserve.DbResourceItem | null);
                if(itemStore === 'EWS') {
                    this.mountEditModeEws(resource.toString(), reserveId.toString())
                } else {
                    this.mountEditDb(resource.toString(), reserveId.toString());
                }   
            });                                    
        } else {
            this.mountWriteMode();
        }
    }   

    get reserveLimit() {
        let limitDate = new Date();
        limitDate.setDate(limitDate.getDate() + 60);
        return uGetDateFormat('yyyy.MM.DD', limitDate);
    }

    mountWriteMode() {
        this.mode = ReserveResourceMode.Write;
        const uid = `uid-${Date.now()}`;
        this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
        this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;

        let promise = this.user.isMailUser ? 
            api.resourceEws.getMyCalendarFolderId() :
            Promise.resolve(null);

        promise.then(res => {                
                this.myCalendarFolderId = res;
                this.mountTimespan();
                this.contentLoaded = true;       
            });   
    }

    mountEditModeEws(resource: string, reserveId: string) { 
        var { open } = this.$route.query;
        const uid = `uid-${Date.now()}`;
        this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
        this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;
        
        this.dispResourceName = this.curResourceItem ? this.curResourceItem.displayName : "";         
        this.masterOccurrence = open ? open.toString() : null;
        Promise.all([
            api.ewsCalendar.getDetail(reserveId, resource),
            api.ewsCalendar.getContent(reserveId, resource)            
        ]).then(res => {                                          
            this.currentItem = res[0] as reserve.CalendarItem;
            this.confSubject = this.currentItem.subject;
           
            let owner = this.currentItem.organizer.address?.toLowerCase() === this.user.email?.toLowerCase();              
            this.mode = owner ? ReserveResourceMode.Edit : ReserveResourceMode.Read
            
            var postProcess: Promise<any> = Promise.resolve();

            if(this.mode == ReserveResourceMode.Edit) {             
                var iCalUid = res[0].iCalUid;

                if(this.masterOccurrence != null && this.masterOccurrence === 'occurrence') {
                    postProcess = api.ewsCalendar.getOriginalAppointment(iCalUid, res[0].start, res[0].end);   
                } else {
                    postProcess = api.ewsCalendar.getOriginalAppointment(iCalUid);
                }                
            }

            postProcess.then(postRes => {             
                var mountItem = res[0];
                this.reserveContent = res[1];

                if(this.mode == ReserveResourceMode.Edit && postRes != null) {                  
                    mountItem = postRes.item;                                                                                                        
                } else {
                    this.mode = ReserveResourceMode.Read                                            
                }

                this.currentReserveId = mountItem.id;
                this.currentItem = mountItem;  
                this.fileCode = mountItem.id;           
                this.calendarFiles = mountItem.attachments.map(a => <ewsResource.EwsAttachment>{
                    ...a,
                    type: 'EWS'
                });              
                                               
                this.mountEditCallback(mountItem);                                    
            }).catch(async (err) => {
                await (window as any).alertAsync(this.$t('예약자의_사서함에_존재하지않아_수정할_수_없습니다').toString());
                this.mode = ReserveResourceMode.Read
                this.mountEditCallback(res[0]);
            });
        }).catch(async (err) => {
            console.error(err);
            await (window as any).alertAsync(this.$t('취소된_일정').toString());
            handleClose(this.isPopup, true);
        })      
    }

    mountEditDb(resource: string, reserveId: string) {
        var { open, sequence } = this.$route.query;        
        this.dispResourceName = this.curResourceItem ? this.curResourceItem.displayName : "";   
        this.masterOccurrence = open ? open.toString() : null;
        this.sequence = sequence ? Number(sequence) : 0;
        const uid = `uid-${Date.now()}`;
        this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
        this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;
                
        let eventPromise: Promise<reserve.CalendarItem> = this.masterOccurrence === 'exception' ?
            api.resourceDb.getCalendarREvent(Number(sequence), Number(reserveId), this.user.personCode) :
            api.resourceDb.getCalendarEvent(Number(reserveId), this.user.personCode);
        
        eventPromise.then(res => {  
            this.currentReserveId = res.id;          
            this.currentItem = res;
            this.confSubject = res.subject;            

            let owner = this.currentItem.organizer.address?.toLowerCase() === this.user.email?.toLowerCase();              
            this.mode = owner ? ReserveResourceMode.Edit : ReserveResourceMode.Read
            this.reserveContent = res.body;
            this.fileCode = res.code;
            appapi.getFiles(res.code).then(fileres => {
                this.calendarFiles = fileres.map(f => <appbase.FileInfo>{
                    ...f,
                    type: 'Database'
                });
                this.mountEditCallback(res);
            });                                      
        }).catch(async (err) => {
            console.error(err);
            await (window as any).alertAsync(this.$t('취소된_일정').toString());
            handleClose(this.isPopup, true);
        })
    }

    mountEditCallback(mountItem: reserve.CalendarItem) {                
        this.mountTimespan(mountItem);
        this.mountAttendees(mountItem);               

        if(typeof(mountItem.recurrence) === 'object') {
            this.mountRecurrence(mountItem.recurrence);   
        }                           
        this.contentLoaded = true;              
    }

    mountTimespan(item?: reserve.CalendarItem) {   
        var { param } = this.$route.query;

        if(param) {
            var decode = atob(decodeURIComponent(param.toString()));
            var reserveParam: reserve.ReserveResourceParam = JSON.parse(decode);

            this.initCompany = reserveParam.comCode;
            this.eventStart = reserveParam.eventStart;
            this.eventEnd = reserveParam.eventEnd;
            this.eventStartTime = reserveParam.eventStartTime;
            this.eventEndTime = reserveParam.eventEndTime;   
            this.initCategory = reserveParam.itemStore;
            this.initResource = reserveParam.resource || null; 
        } else {    
            if(item) {
                this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(item.start));
                this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(item.end));
                this.eventStartTime = uGetDateFormat('H:i:s', IEDate(item.start));
                this.eventEndTime = uGetDateFormat('H:i:s', IEDate(item.end));
                this.dispEventTime = getFullTimetext(item);
                this.isAlldayEvent = item.isAllDayEvent && item.isAllDayEvent.toString().toLowerCase() === 'true';
            } else {
                var timeNow = new Date();
                if(timeNow.getMinutes() >= 30) {                  
                    timeNow.setHours(timeNow.getHours() + 1);
                    this.eventStartTime = uGetDateFormat("H:00:00", timeNow);  
                    this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));              
                    
                    timeNow.setHours(timeNow.getHours() + 1);                  
                    this.eventEndTime = uGetDateFormat("H:00:00", timeNow);
                    this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));
                } else {
                    this.eventStartTime = uGetDateFormat("H:30:00", timeNow);  
                    this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));   
                    timeNow.setHours(timeNow.getHours() + 1);         
                    this.eventEndTime = uGetDateFormat("H:30:00", timeNow);
                    this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));
                }    
            }
        }                                   
    }   

    mountAttendees(item: reserve.CalendarItem) {
        this.requiredRecipients = item.requiredAttendees.map(a => <orgmap.UserItem>{
            displayName: a.name,
            titleName: "",
            deptName: a.address,
            email: a.address
        });

        this.optionalRecipients = item.optionalAttendees.map(a => <orgmap.UserItem>{
            displayName: a.name,
            titleName: "",
            deptName: a.address,
            email: a.address
        });
    }

    mountRecurrence(recurrence: any) {
        var recurringEndType = '1';

        if (recurrence.hasEnd) {
            if (recurrence.numberOfOccurrences != null) {
                recurringEndType = '2'
            } else {
                recurringEndType = '3'
            }
        }

        this.currentReccuringOption = <reserve.CalendarItemRecurrence>{
            daysOfTheWeekArray: recurrence.daysOfTheWeekArray,
            startDate: uGetDateFormat('yyyy-MM-dd', IEDate(recurrence.startDate)),
            endDate: recurrence.endDate != null ? uGetDateFormat('yyyy-MM-dd', IEDate(recurrence.endDate)) : null,
            interval: recurrence.interval,
            numberOfOccurrences: recurrence.numberOfOccurrences,
            ui_endType: recurringEndType,
            ui_recurringType: this.currentItem?.pidLidRecurrenceType || 1,
            dayOfMonth: recurrence.dayOfMonth || "",
            monthValue: recurrence.monthValue || 1
        }

        this.updateRecurrenceDesc();
    }

    getItemStore(): 'EWS' | 'DB' {       
        if(!isNaN(Number(this.currentItem?.id))) {
            return 'DB'
        } else {
            return 'EWS'
        }
    }

    onTimespanChanged(startDate: string, startTime: string, endDate: string, endTime: string) {
        if(this.contentLoaded) {          
            this.updateRecurrenceDesc();
        } 
    }   

    emitContent(): string {
        const editor: editor.IEditorProxy = this.$refs.editor as any 
        return editor.getEditor().getBodyValue();
    }

    fileUploadStatusChaged(data: any) {
        if (data === 'uploading') {
            this.isFileUploading = true
        } else {
            this.isFileUploading = false
        }
    }

    async onCancelClick() {     
        if (await(window as any).confirmAsync(this.$t('W_Mail_LeaveWrite'))) handleClose(this.isPopup);
    }

    async checkRequestValidate(comCode: string, itemStore: 'EWS' | 'DB' | 'FAVORITE', requestResource: string, requestItem: reserve.CalendarItem): Promise<boolean> {        
        var current = new Date();
        var { reserveId } = this.$route.query;
        var checkStart = IEDate(`${requestItem.start} ${requestItem.starttime}`);
        var checkEnd = IEDate(`${requestItem.end} ${requestItem.endtime}`);       

        if(requestItem.subject == null || requestItem.subject.trim().length == 0) {
            (window as any).alertAsync(this.$t('예약_제목을_입력하세요').toString());
            return false;
        }

        if(checkStart.getTime() <= current.getTime()) {
            (window as any).alertAsync(this.$t('회의실_과거예약').toString());        
            return false;
        }

        if ((checkStart.getTime() - checkEnd.getTime())/(1000 * 60) > 1440) {
            return true;
        }

        var availability = await api.resource.getResourceAvailable({              
            itemStore: itemStore,
            resource: requestResource,
            start: checkStart,
            end: checkEnd,       
            originalId: this.mode == ReserveResourceMode.Edit ? reserveId.toString() : undefined              
        })

        if(!availability.available) {
            (window as any).alertAsync(this.$t('회의실_사용시간중복').toString());
            return false;
        }

        return true;
    }

    getRequestModel(requestResource: ewsResource.EwsResourceItem | reserve.DbResourceItem, myCalendarFolderId: string | null): reserve.CalendarItem {
        var requestItem: reserve.CalendarItem = {
            parentId: '',
            //attachments: [] as any,
            hasAttachments: false,
            body: '',
            location: '',
            subject: '',
            status: 'S0',
            appointmentType: 'Single',
            pidLidRecurrenceType: 1,
            ui_resourceArray: [] as Array<any>,
            resourceId: null,
            resources: [] as Array<any>,
            recurrence: null,
            code: this.uploaderConfig.code,
            codeValues: this.uploaderConfig.codeValues,
            importance: 'Normal',
            isCancelled: false,
            isRecurring: false,
            isAllDayEvent: false,
            isAllowUpdate: true,
            start: uGetDateFormat('yyyy-MM-DDTH:i:00', new Date()),
            end: uGetDateFormat('yyyy-MM-DDTH:i:00', new Date()),
            requiredAttendees: [] as any,
            optionalAttendees: [] as any,
            isReadyData: false,
            delegateUser: '',
            reminderMinutesBeforeStart: 15            
        } as reserve.CalendarItem;

        requestItem.subject = this.confSubject;
        requestItem.body = this.emitContent();                       
        requestItem.importance = "Normal";
        requestItem.appointmentType = 'Single';
        requestItem.isRecurring = false;
        requestItem.reminderMinutesBeforeStart
        requestItem.isAllDayEvent = this.isAlldayEvent;
        requestItem.start = this.eventStart;
        requestItem.starttime = this.isAlldayEvent ? "00:00:00" : this.eventStartTime;
        requestItem.end = this.eventEnd;
        requestItem.endtime = this.isAlldayEvent ? "00:00:00" : this.eventEndTime;
        requestItem.location = requestResource.displayName;
        requestItem.requiredAttendees = this.requiredRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });
        requestItem.optionalAttendees = this.optionalRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });
                    
        if(requestResource.itemStore === 'EWS') {
            requestItem.parentId = myCalendarFolderId;
            requestItem.requiredAttendees.push({
                name: requestResource.displayName,
                address: requestResource.address
            }); 
        } else {            
            requestItem.parentId = (requestResource as reserve.DbResourceItem).folderId?.toString();
            requestItem.organizer = {
                name: this.user.displayName,
                address: this.user.email
            }
        }                 

        if(requestItem.isAllDayEvent && (requestItem.start === requestItem.end)) {
            var changeEnd = IEDate(requestItem.end);
            changeEnd.setDate(changeEnd.getDate() + 1);
            requestItem.end = uGetDateFormat('yyyy-MM-dd', changeEnd);
        }

        if(this.currentReccuringOption != null) {
            requestItem.isRecurring = true;
            requestItem.recurrence = this.currentReccuringOption;
            requestItem.appointmentType = 'RecurringMaster';           
        } else {
            requestItem.isRecurring = false;
            requestItem.recurrence = null;
        }
            
        return requestItem;
    }

    async onReserveClick() {            
        if(this.curResourceItem == null) {
            (window as any).alertAsync(this.$t('선택된_자원이_없습니다'));
            return;
        }

        var requestResource = this.curResourceItem;                                  
        this.isLoading = true;
        let requestItem = this.getRequestModel(requestResource, this.myCalendarFolderId);

        var validate: boolean = true;

        if(!this.isAlldayEvent) {
            validate = await this.checkRequestValidate(this.initCompany, requestResource.itemStore, requestResource.address, requestItem);
        } 
    
        if(validate) {
            this.createEvent({ 
                item: requestItem, 
                itemStore: requestResource.itemStore 
            }).then(async res => {
                await (window as any).alertAsync(this.$t('예약신청_완료').toString());
                handleClose(this.isPopup, true);
            })    
        } else {                
            this.isLoading = false;
        }                                         
    }

    updateRecurrenceDesc() {        
        if(this.currentReccuringOption != null) {
            var constMonthArray: string[] = [
                this.$t('W_1M').toString(),
                this.$t('W_2M').toString(),
                this.$t('W_3M').toString(),
                this.$t('W_4M').toString(),
                this.$t('W_5M').toString(),
                this.$t('W_6M').toString(),
                this.$t('W_7M').toString(),
                this.$t('W_8M').toString(),
                this.$t('W_9M').toString(),
                this.$t('W_10M').toString(),
                this.$t('W_11M').toString(),
                this.$t('W_12M').toString()
            ];

            var constWeekArray: string[] = [
                this.$t('W_Schedule_Sunday').toString(),
                this.$t('W_Schedule_Monday').toString(),
                this.$t('W_Schedule_Tuesday').toString(),
                this.$t('W_Schedule_Wednesday').toString(),
                this.$t('W_Schedule_Thursday').toString(),
                this.$t('W_Schedule_Friday').toString(),
                this.$t('W_Schedule_Saturday').toString()
            ];

            var dispTimeArray = uGetTimeArray([
                this.$t('W_AM').toString(),
                this.$t('W_PM').toString()
            ]);

            var idxStartTime = dispTimeArray.findIndex(t => t.value === this.eventStartTime);
            var idxEndTime = dispTimeArray.findIndex(t => t.value === this.eventEndTime);
            var dispHour = (idxEndTime - idxStartTime) * 0.5;
            var timeSpan = this.isAlldayEvent ?
                this.$t('W_Schedule_Allday').toString() :
                `${dispTimeArray[idxStartTime].text} ~ ${dispTimeArray[idxEndTime].text} (${dispHour}${this.$t('W_Hour').toString()})`;
            var prefix = "";

            switch (this.currentReccuringOption.ui_recurringType) {
                case 1:
                    prefix = this.$t('매N일마다').toString().replace('{INTERVAL}', this.currentReccuringOption.interval.toString());
                    break;
                case 2:
                    var dayJoin = this.currentReccuringOption.daysOfTheWeekArray?.map(i => constWeekArray[i]).join(",");
                    prefix = this.$t('매N주').toString()
                        .replace('{INTERVAL}', this.currentReccuringOption.interval.toString())
                        .replace('{DAYJOINS}', dayJoin || "");
                    break;
                case 3:
                    prefix = this.$t('매N월').toString()
                        .replace('{INTERVAL}', this.currentReccuringOption.interval.toString())
                        .replace('{DAYOFMONTH}', this.currentReccuringOption.dayOfMonth?.toString() || "");
                    break;
                case 4:
                    prefix = this.$t('매년N월').toString()
                        .replace('{MONTH}', constMonthArray[this.currentReccuringOption.monthValue - 1])
                        .replace('{DAYOFMONTH}', this.currentReccuringOption.dayOfMonth?.toString() || "");
                    break;
            }

            this.recurrenceDesc = this.$t('반복일정_설명').toString().replace('{PREFIX}', prefix).replace('{TIMESPAN}', timeSpan);

            switch (this.currentReccuringOption.ui_endType) {
                case "1":
                    this.recurrenceIntervalDesc = this.$t('N부터_계속반복됩니다').toString().replace('{START}', this.currentReccuringOption.startDate);
                    break;
                case "2":
                    this.recurrenceIntervalDesc = this.$t('N회_반복후종료').toString().replace('{OCCURRENCE_NUMBER}', this.currentReccuringOption.numberOfOccurrences?.toString() || "");
                    break;
                case "3":
                    this.recurrenceIntervalDesc = `${this.currentReccuringOption.startDate} ~ ${this.currentReccuringOption.endDate}`;
                    break;
            }
        }
    }

    onRecurringClick() {
        this.showRecurringOption = true;
    }

    onRecurringOptionSave(option: reserve.CalendarItemRecurrence) {
        this.currentReccuringOption = option;
        this.currentReccuringOption.startDate = this.eventStart;
        this.updateRecurrenceDesc();
        this.showRecurringOption = false;
    }

    onReccuringOptionClose() {
        this.showRecurringOption = false;
    }

    onRemoveRecurrenceClick() {
        this.currentReccuringOption = null;
    }

    async onUpdateReserveClick() {
        if(this.curResourceItem == null) {
            (window as any).alertAsync(this.$t('선택된_자원이_없습니다'));
            return;
        }
            
        var { resource } = this.$route.query;
        this.isLoading = true;
        
        var updateResource = resource.toString();
        var requestItem = { ...this.currentItem } as reserve.CalendarItem;               

        requestItem.code = this.uploaderConfig.code
        requestItem.codeValues = this.uploaderConfig.codeValues;
        requestItem.subject = this.confSubject;
        requestItem.status = 'S0',
        requestItem.isAllDayEvent = this.isAlldayEvent;
        requestItem.start = this.eventStart;
        requestItem.body = this.emitContent();
        requestItem.starttime = this.isAlldayEvent ? "00:00:00" : this.eventStartTime;
        requestItem.end = this.eventEnd;
        requestItem.endtime = this.isAlldayEvent ? "00:00:00" : this.eventEndTime;
        requestItem.requiredAttendees = this.requiredRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });
        requestItem.optionalAttendees = this.optionalRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });        
        
        if(requestItem.isAllDayEvent && (requestItem.start === requestItem.end)) {
            var changeEnd = IEDate(requestItem.end);
            changeEnd.setDate(changeEnd.getDate() + 1);
            requestItem.end = uGetDateFormat('yyyy-MM-dd', changeEnd);
        }

        if (this.currentReccuringOption != null) {
            requestItem.isRecurring = true;
            requestItem.recurrence = this.currentReccuringOption;
        }

        if(this.masterOccurrence !== 'master' && this.curResourceItem.itemStore === 'DB') {
            requestItem.db_masterID = Number(requestItem.id);
            requestItem.id = this.sequence.toString();
            requestItem.appointmentType = 'Exception';        
        }

        var validate = await this.checkRequestValidate(this.initCompany, this.curResourceItem.itemStore, updateResource, requestItem);

        if (validate) {
            this.updateEvent({ 
                    item: requestItem, 
                    itemStore: this.curResourceItem.itemStore,
                    masterOccurrence: this.masterOccurrence 
            }).then(async res => {
                await (window as any).alertAsync(this.$t('예약신청_업데이트').toString());
                handleClose(this.isPopup, true);
            })
        } else {
            this.isLoading = false;
        }
    }

    async onCancelReserveClick() {                
        if(this.currentItem && this.curResourceItem != null && await (window as any).confirmAsync(this.$t('예약삭제_확인').toString())) {
            this.isLoading = true;
            var cancelPromise: Promise<any> = Promise.resolve();

            if(this.curResourceItem.itemStore === 'EWS') {
                cancelPromise = this.cancelEwsEvent(this.currentItem.id);
            } else {
                cancelPromise = this.cancelDbEvent(this.currentItem);
            }

            cancelPromise.then(async res => {
                await (window as any).alertAsync(this.$t('예약삭제_안내').toString());
                handleClose(this.isPopup, true);
            })                
        }
    }

    onNewWindowClick() {
        var { comCode, itemStore, reserveId, resource } = this.$route.query;

        openReserveWindow({
            comCode: comCode?.toString(),
            category: itemStore.toString(),
            reserveId: reserveId.toString(),
            resource: resource.toString()
        })   
    }

    onFileRemoved(file: ewsResource.EwsAttachment | appbase.FileInfo, idx: number) {
        this.calendarFiles.splice(idx, 1);        
    }

}